import React, { useState } from "react";
import CloseIcon from '@mui/icons-material/Close';
import { IconButton } from '@mui/material';
import CircularProgress from '@mui/material/CircularProgress';
import TextField from '@mui/material/TextField';
import PostData from '../../services/PostData.js'
import CheckOutlinedIcon from '@mui/icons-material/CheckOutlined';
import InputAdornment from '@mui/material/InputAdornment';

const formatDate = (dateString) => {
    if (!dateString) return '';
    // If already looks like a date string from CSV, keep it
    if (typeof dateString === 'string' && (dateString.includes('/') || dateString.includes('-'))) {
        return dateString;
    }
    const date = new Date(dateString);
    if (Number.isNaN(date.getTime())) return String(dateString);
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${month}-${day}-${year}`;
};

export default function SpreadsheetResponse({ responseData, closeDialog }) {
    const [submitLoad, setSubmitLoad] = useState(false)
    const [successUpload, setSuccessUpload] = useState(false)
    const [inputVals, setInputVals] = useState([
        { field: "Invoice ID", data: responseData?.invoiceId ?? '', name: "invoice_id" },
        {
            field: "Supplier Name",
            data: responseData?.supplierName || responseData?.supplierDisplayName || '',
            name: "supplier_name",
        },
        {
            field: "Date",
            data: responseData?.invoiceDate
                ? formatDate(responseData.invoiceDate)
                : formatDate(new Date()),
            name: "invoice_date",
        },
    ])

    const [inputItems, setInputItems] = useState(responseData?.invoiceData?.map(item => ({
        description: item?.description ?? '',
        item_number: item?.item_number ?? '',
        qty: Number(item?.qty) || 1,
        price: Number(item?.price) || 0,
    })) || [])

    const handleInputChanges = (e, fieldName) => {
        const { value } = e.target;
        setInputVals(prevVals => prevVals.map(val =>
            val.name === fieldName ? { ...val, data: value } : val
        ));
    }

    const handleItemInputChanges = (e, index, fieldName) => {
        const { value } = e.target;
        setInputItems(prevItems => {
            const newItems = [...prevItems];
            newItems[index] = { ...newItems[index], [fieldName]: value };
            return newItems;
        });
    };

    const handleSubmit = async () => {
        setSubmitLoad(true)
        const submitVal = {
            headers: inputVals,
            items: inputItems,
            configId: responseData?.configId,
        }
        const { success, error } = await PostData(submitVal, "broylesllc/confirm-csv-invoice-data", null)
        setSubmitLoad(false)
        setSuccessUpload(true)
        if (error?.response?.status === 409) {
            setTimeout(() => closeDialog("Exists"), 800)
        } else if (success) {
            setTimeout(() => closeDialog("Success"), 800)
        }
    }

    const handleClose = () => closeDialog()

    return (
        <div style={{
            height: '80vh', display: 'flex',
            flexDirection: 'column', minWidth: '550px', overflowX: "hidden"
        }}>
            <div className='row' style={{
                width: "100%", padding: "20px 0 10px 10px", position: "sticky",
                top: 0, zIndex: 100, background: "white",
                borderBottom: 'solid lightgray', margin: 0
            }}>
                <div className='col-9'>
                    <h4 style={{ marginBottom: "0" }}>Invoice Confirmation: </h4>
                </div>
                <div>
                    <IconButton
                        onClick={handleClose}
                        sx={{
                            position: 'absolute', width: '40px', height: '40px', top: '10px', right: '30px',
                            boxShadow: "rgba(0, 0, 0, 0.16) 0px 1px 4px",
                            backgroundColor: 'white', zIndex: 100, "&:hover": { backgroundColor: '#f0f0f0' }
                        }}
                    >
                        <CloseIcon sx={{ cursor: 'pointer' }} />
                    </IconButton>
                </div>
            </div>
            <div style={{ padding: '0 10px' }}>
                <div>
                    {inputVals.map((val, ind) =>
                        <div className='row m-0' key={ind}>
                            <label style={{ padding: '10px 0 5px 5px', width: 'max-content' }}>{val.field}</label>
                            <TextField
                                size="small"
                                name={val.name}
                                value={val.data}
                                onChange={(e) => handleInputChanges(e, val.name)}
                                sx={{ width: '100%' }} />
                        </div>
                    )}
                </div>
                <div>
                    {inputItems.map((item, itemIndex) => (
                        <div key={itemIndex} style={{ border: '1px solid #e0e0e0', padding: '10px', marginBottom: '10px', marginTop: '10px', borderRadius: '5px' }}>
                            <h5 style={{ marginBottom: '10px' }}>Item {itemIndex + 1}</h5>
                            <div className='row m-0'>
                                <label style={{ padding: '10px 0 5px 5px', width: 'max-content' }}>Name</label>
                                <TextField
                                    size="small"
                                    name='description'
                                    value={item.description}
                                    onChange={(e) => handleItemInputChanges(e, itemIndex, 'description')}
                                    sx={{ width: '100%' }}
                                />
                            </div>
                            <div className='row m-0'>
                                <label style={{ padding: '10px 0 5px 5px', width: 'max-content' }}>Item Number</label>
                                <TextField
                                    size="small"
                                    name='item_number'
                                    value={item.item_number}
                                    onChange={(e) => handleItemInputChanges(e, itemIndex, 'item_number')}
                                    sx={{ width: '100%' }}
                                />
                            </div>
                            <div className='row m-0'>
                                <label style={{ padding: '10px 0 5px 5px', width: 'max-content' }}>Quantity</label>
                                <TextField
                                    size="small"
                                    name='qty'
                                    value={item.qty}
                                    onChange={(e) => handleItemInputChanges(e, itemIndex, 'qty')}
                                    sx={{ width: '100%' }}
                                />
                            </div>
                            <div className='row m-0'>
                                <label style={{ padding: '10px 0 5px 5px', width: 'max-content' }}>Unit Price</label>
                                <TextField
                                    size="small"
                                    name='price'
                                    value={item.price}
                                    onChange={(e) => handleItemInputChanges(e, itemIndex, 'price')}
                                    sx={{ width: '100%' }}
                                    InputProps={{
                                        startAdornment: (
                                            <InputAdornment position="start">$</InputAdornment>
                                        ),
                                    }}
                                />
                            </div>
                        </div>
                    ))}
                </div>
            </div>
            <div className='row m-0'
                style={{ position: 'sticky', bottom: 0, backgroundColor: 'white', padding: '10px 10px', borderTop: '1px solid #e0e0e0', justifyContent: 'end' }}>
                {successUpload ?
                    <button className='submit'
                        style={{ width: '205px', background: '#049e04', height: '35px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        <span>Success</span> <CheckOutlinedIcon fontSize="small" sx={{ color: 'white', marginLeft: '10px' }} />
                    </button>
                    :
                    (!submitLoad ? <button className='submit' type='submit' onClick={handleSubmit}
                        style={{ width: '205px', height: '35px' }}>
                        Submit
                    </button> :
                        <button className='submit' type='submit'
                            style={{ width: '205px', height: '35px' }}>
                            <CircularProgress size={20} sx={{ color: 'white', marginTop: '5px' }} />
                        </button>)
                }
            </div>
        </div>
    )
}
